import React from 'react';
import { motion } from 'framer-motion';
import { Gift, Star, Zap, ShoppingBag, ArrowRight, Trophy } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useIsMobile } from '../../hooks/useIsMobile';

const G    = '#0F5A3A';
const GOLD = '#F5B327';
const IVORY = '#F4F0E6';
const BTN_R = '8px';

const EARN_ITEMS = [
  { label: 'Complete your profile', pts: '+150 waves', icon: <Star size={14} color={GOLD} /> },
  { label: 'Connect with a college rep', pts: '+50 waves', icon: <Zap size={14} color={GOLD} /> },
  { label: 'Refer a friend', pts: '+200 waves', icon: <Trophy size={14} color={GOLD} /> },
  { label: 'Apply to scholarships', pts: '+75 waves', icon: <Gift size={14} color={GOLD} /> },
];

const REDEEM_ITEMS = [
  { label: 'Amazon Gift Card', amount: '$10', waves: '500 waves', bg: '#FF9900' },
  { label: 'SS Merch — Hoodie', amount: 'Hoodie', waves: '800 waves', bg: G },
  { label: 'Starbucks Card', amount: '$5', waves: '300 waves', bg: '#00704A' },
  { label: 'App Store Credit', amount: '$15', waves: '750 waves', bg: '#0071e3' },
];

export const SignalWaves = () => {
  const isMobile = useIsMobile();

  return (
    <section style={{ background: IVORY, padding: isMobile ? '48px 0' : '80px 0', position: 'relative' }} data-testid="signal-waves-section">
      <div style={{ maxWidth: '1180px', margin: '0 auto', padding: isMobile ? '0 20px' : '0 40px' }}>
        <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} style={{ marginBottom: isMobile ? '28px' : '40px' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em', color: GOLD, marginBottom: '12px' }}>Signal Waves Rewards</div>
          <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: isMobile ? '1.75rem' : 'clamp(1.9rem, 3vw, 2.7rem)', fontWeight: 900, color: G, lineHeight: 1.1, letterSpacing: '-0.02em', margin: '0 0 12px' }}>
            Earn rewards for building<br />your future.
          </h2>
          <p style={{ fontSize: '1rem', color: '#5A6B5E', lineHeight: 1.7, maxWidth: '520px' }}>
            Every action you take on StudentSignal earns Signal Waves — complete your profile, engage with the platform, refer friends, and redeem them for merchandise, gift cards, coupons, and rewards.
          </p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.65, delay: 0.1 }}
          style={{ background: G, borderRadius: '20px', padding: isMobile ? '24px 20px' : '40px', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '320px', height: '320px', borderRadius: '50%', background: `radial-gradient(circle, ${GOLD}14 0%, transparent 70%)`, pointerEvents: 'none' }} />

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '20px' : '28px', position: 'relative', zIndex: 1 }}>
            {/* Earn */}
            <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: '16px', padding: isMobile ? '20px' : '28px', border: '1px solid rgba(255,255,255,0.1)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: `${GOLD}25`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Zap size={18} color={GOLD} /></div>
                <div>
                  <div style={{ fontSize: '15px', fontWeight: 800, color: '#fff' }}>Earn Signal Waves</div>
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>Take action, earn rewards</div>
                </div>
              </div>
              {EARN_ITEMS.map((item) => (
                <div key={item.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', borderRadius: '10px', background: 'rgba(255,255,255,0.05)', marginBottom: '7px', border: '1px solid rgba(255,255,255,0.07)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                    <div style={{ width: '26px', height: '26px', borderRadius: '7px', background: `${GOLD}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{item.icon}</div>
                    <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.82)' }}>{item.label}</span>
                  </div>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: GOLD, background: `${GOLD}18`, padding: '2px 9px', borderRadius: '4px', whiteSpace: 'nowrap', flexShrink: 0, marginLeft: '8px' }}>{item.pts}</span>
                </div>
              ))}
              <div style={{ marginTop: '14px', padding: '12px 14px', borderRadius: '12px', background: `${GOLD}15`, border: `1px solid ${GOLD}28`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.65)' }}>Your current waves</span>
                <span style={{ fontSize: '20px', fontWeight: 900, color: GOLD }}>1,450</span>
              </div>
            </div>

            {/* Redeem */}
            <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: '16px', padding: isMobile ? '20px' : '28px', border: '1px solid rgba(255,255,255,0.1)', display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: `${GOLD}25`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><ShoppingBag size={18} color={GOLD} /></div>
                <div>
                  <div style={{ fontSize: '15px', fontWeight: 800, color: '#fff' }}>Redeem Rewards</div>
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>Real value, real rewards</div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '9px', marginBottom: '16px', flex: 1 }}>
                {REDEEM_ITEMS.map((item) => (
                  <div key={item.label} style={{ padding: '12px', borderRadius: '12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.09)', cursor: 'pointer', transition: 'background 0.2s' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}>
                    <div style={{ width: '28px', height: '28px', borderRadius: '7px', background: item.bg, marginBottom: '8px' }} />
                    <div style={{ fontSize: '12px', fontWeight: 700, color: '#fff', marginBottom: '2px' }}>{item.label}</div>
                    <div style={{ fontSize: '12px', color: GOLD, fontWeight: 700 }}>{item.amount}</div>
                    <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', marginTop: '3px' }}>{item.waves}</div>
                  </div>
                ))}
              </div>
              <Link to="/campus-connect" data-testid="signal-waves-cta"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '13px', background: GOLD, color: '#1a1a1a', fontWeight: 700, fontSize: isMobile ? '13px' : '14px', borderRadius: BTN_R, textDecoration: 'none', transition: 'opacity 0.2s' }}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}>
                Start Earning Signal Waves Rewards <ArrowRight size={15} />
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SignalWaves;
